import { useEffect, useMemo, useRef, useState } from "react";
import type { CartState } from "../lib/cart";
import type { GroceryItem } from "../lib/items";

const LOADING_MESSAGES = [
  "Preheating the vibe oven…",
  "Toasting personality breadcrumbs…",
  "Seasoning your aura with a pinch of salt…",
  "Sautéing some spicy character development…",
  "Whisking your traits until glossy…",
  "Simmering thoughts on low heat…",
  "Plating your inner garnish…",
  "Marinating your main character energy…",
  "Microwaving confidence (carefully)…",
  "Emulsifying your chaos into something delicious…",
  "Proofing your opinions (they're rising)…",
  "Reducing your overthinking into a sauce…",
  "Caramelizing your impulses…",
  "Chopping onions (for emotional depth)…",
  "Blending quirks into a smooth purée…",
  "Rolling your vibes into little dumplings…",
  "Kneading self-awareness—elastic, not sticky…",
  "Folding in the dramatic flair…",
  "Sprinkling cinnamon on your decisions…",
  "Grating existential parmesan…",
  "Shucking the awkwardness…",
  "Cracking eggs of wisdom…",
  "Stirring the pot (respectfully)…",
  "Steeping your intuition like tea…",
  "Fermenting bold takes…",
  "Basting with a buttery optimism…",
  "Tossing your mood in a salad spinner…",
  "Skimming the foam of doubt…",
  "Turning leftovers into brilliance…",
  "Toast-checking your resilience…",
  "Spiralizing your plans…",
  "Seeding your thoughts (no watermelon incidents)…",
  "Grinding beans for maximum focus…",
  "Chilling the drama in the freezer…",
  "Warming your heart on the back burner…",
  "Shaking in some umami confidence…",
  "Opening a jar of spontaneity…",
  "Sampling for seasoning adjustments…",
  "Rolling out boundaries like fresh dough…",
  "Flambéing the self-doubt (watch your eyebrows)…",
  "Dusting flour on your intentions…",
  "Packing snacks for the emotional journey…",
  "Slicing neat little coping mechanisms…",
  "Smoothing frosting on your public persona…",
  "Adding crunch: one brave choice…",
  "Pouring a tall glass of calm…",
  "Stacking pancakes of possibility…",
  "Bubbling up a fizzy idea…",
  "Garnishing with microgreens of hope…",
  "Tuning the timer—almost ready to serve…",
];

export type CheckoutModalProps = {
  open: boolean;
  cart: CartState;
  itemsById: Map<string, GroceryItem>;
  onClose: () => void;
};

interface PersonalityResponse {
  personalityType: string;
  description: string;
}

// Production API URL, falls back to localhost for development
const API_URL = process.env.REACT_APP_API_URL || "https://grocery-personality-test-server.noshado.ws";

function formatPersonalityType(type: string): string {
  return type
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function CheckoutModal({ open, cart, itemsById, onClose }: CheckoutModalProps) {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const cardTiltRef = useRef<HTMLDivElement | null>(null);
  const tiltRafRef = useRef<number | null>(null);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingTextIdx, setLoadingTextIdx] = useState(0);
  const [personality, setPersonality] = useState<PersonalityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get image URL based on personality type
  const imageUrl = useMemo(() => {
    if (!personality?.personalityType) return null;
    return `${process.env.PUBLIC_URL || ""}/personality-cards/${personality.personalityType}.jpeg`;
  }, [personality]);

  const cartItems = useMemo(
    () =>
      Object.keys(cart)
        .filter((id) => (cart[id] ?? 0) > 0 && itemsById.has(id))
        .slice(0, 10)
        .map((id) => itemsById.get(id)!)
        .filter(Boolean),
    [cart, itemsById]
  );
  const cartItemsKey = useMemo(() => cartItems.map((item) => item.id).sort().join(","), [cartItems]);

  const updateTilt = () => {
    tiltRafRef.current = null;
    const el = cardTiltRef.current;
    const last = lastPointerRef.current;
    if (!el || !last) return;

    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    // Map global cursor position into the card's local space; clamp so moving outside the card
    // still produces a stable, subtle max-tilt effect instead of exploding values.
    const px = Math.min(1, Math.max(0, (last.x - rect.left) / rect.width)); // 0..1
    const py = Math.min(1, Math.max(0, (last.y - rect.top) / rect.height)); // 0..1
    const dx = px - 0.5; // -0.5..0.5
    const dy = py - 0.5;
    const maxDeg = 7; // subtle
    const ry = dx * (maxDeg * 2);
    const rx = -dy * (maxDeg * 2);
    const dist = Math.min(1, Math.sqrt(dx * dx + dy * dy) / 0.75); // 0..1
    const shine = Math.max(0, 1 - dist);
    // Fixed top light: move the highlight based on tilt (not cursor), so it feels like a
    // consistent light source above the card.
    const gx = Math.min(90, Math.max(10, 50 - dx * 80)); // %
    const gy = Math.min(35, Math.max(6, 12 - dy * 50)); // %
    // Offsets used for dramatic lighting streaks (background-position).
    const txp = Math.max(-22, Math.min(22, dx * 44)); // %
    const typ = Math.max(-14, Math.min(14, dy * 28)); // %

    el.style.setProperty("--rx", `${rx.toFixed(2)}deg`);
    el.style.setProperty("--ry", `${ry.toFixed(2)}deg`);
    el.style.setProperty("--gx", `${gx.toFixed(1)}%`);
    el.style.setProperty("--gy", `${gy.toFixed(1)}%`);
    el.style.setProperty("--txp", `${txp.toFixed(1)}%`);
    el.style.setProperty("--typ", `${typ.toFixed(1)}%`);
    el.style.setProperty("--shine", `${shine.toFixed(3)}`);
  };

  const resetTilt = () => {
    lastPointerRef.current = null;
    const el = cardTiltRef.current;
    if (!el) return;
    el.style.setProperty("--rx", `0deg`);
    el.style.setProperty("--ry", `0deg`);
    el.style.setProperty("--gx", `50%`);
    el.style.setProperty("--gy", `12%`);
    el.style.setProperty("--txp", `0%`);
    el.style.setProperty("--typ", `0%`);
    el.style.setProperty("--shine", `0`);
  };

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      // Reset state when modal closes
      setPersonality(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setPersonality(null);
    setLoadingTextIdx(Math.floor(Math.random() * LOADING_MESSAGES.length));
    resetTilt();

    // Fetch personality from server
    const fetchPersonality = async () => {
      try {
        const itemNames = cartItems.map((item) => item.name);

        const response = await fetch(`${API_URL}/api/personality`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items: itemNames }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch personality: ${response.statusText}`);
        }

        const data: PersonalityResponse = await response.json();
        setPersonality(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching personality:", err);
        setError(err instanceof Error ? err.message : "Failed to generate personality");
        setLoading(false);
      }
    };

    fetchPersonality();
  }, [open, cartItemsKey, cartItems]);

  // Drive the 3D tilt/shimmer from global cursor movement (anywhere on the page),
  // while the modal is open and showing the card.
  useEffect(() => {
    if (!open || loading || !imageUrl || !personality) return;
    const onMove = (e: MouseEvent) => {
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
      if (tiltRafRef.current != null) return;
      tiltRafRef.current = window.requestAnimationFrame(updateTilt);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [open, loading, imageUrl, personality]);

  useEffect(() => {
    if (!open || !loading) return;
    const id = window.setInterval(() => {
      setLoadingTextIdx((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 1000);
    return () => window.clearInterval(id);
  }, [open, loading]);

  useEffect(() => {
    if (!open) return;
    // Focus something inside so keyboard users don't get stranded behind the modal.
    closeBtnRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      if (tiltRafRef.current != null) {
        window.cancelAnimationFrame(tiltRafRef.current);
        tiltRafRef.current = null;
      }
      resetTilt();
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="checkoutModalOverlay"
      role="dialog"
      aria-modal="true"
      aria-label="Checkout"
      onMouseDown={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <div className="checkoutModalPanel">
        <button
          ref={closeBtnRef}
          className="checkoutModalClose iconBtn"
          type="button"
          onClick={onClose}
          aria-label="Close checkout"
        >
          ✕
        </button>

        <div className={`checkoutModalBody ${loading ? "checkoutModalBodyLoading" : "checkoutModalBodyLoaded"}`}>
          {loading ? (
            <div className="checkoutModalLoading" aria-live="polite" aria-busy="true">
              <div className="checkoutSpinner" aria-hidden="true" />
              <div className="checkoutLoadingText">{LOADING_MESSAGES[loadingTextIdx] ?? "Generating…"}</div>
            </div>
          ) : personality && imageUrl ? (
            <div className="checkoutModalContent">
              <div className="checkoutModalLeft">
                <div
                  ref={cardTiltRef}
                  className="checkoutCard3d"
                >
                  <div className="checkoutCard3dInner">
                    <img
                      className="checkoutModalImage"
                      src={imageUrl}
                      alt="Personality card"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  </div>
                </div>
              </div>

              <div className="checkoutModalRight">
                {personality ? (
                  <>
                    <div className="checkoutModalTitle">{formatPersonalityType(personality.personalityType)}</div>
                    <div className="checkoutModalDescription">{personality.description}</div>
                  </>
                ) : error ? (
                  <>
                    <div className="checkoutModalTitle">Oops!</div>
                    <div className="checkoutModalDescription">
                      {error}. Please try again later.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="checkoutModalTitle">Your grocery personality</div>
                    <div className="checkoutModalDescription">
                      Based on the 10 items you picked, here's your totally scientific, mildly dramatic result.
                    </div>
                  </>
                )}

                <ul className="checkoutModalCartList" aria-label="Cart items used for checkout">
                  {cartItems.map((item) => (
                    <li className="checkoutModalCartItem" key={item.id}>
                      <img className="checkoutModalCartThumb" src={item.imageUrl} alt={item.name} />
                      <span className="checkoutModalCartName">{item.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
